import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  CalendarMonth,
  CheckCircle,
  Diversity3,
  Groups,
  LocationOn,
  PersonAddAlt,
} from "@mui/icons-material";

import "../styles/community-meetups.css";

const interestOptions = [
  "Second income / entrepreneurship",
  "Student internship / mentor",
  "Immigration help: O-1 / NIW / EB-1A",
  "Business or enterprise partnership",
  "Finance / insurance / family planning",
  "Healthcare / Ayurveda / wellness",
  "Education / art / music / tutoring",
  "Food / catering / baking",
  "Priest / ritual services",
  "Volunteer or community leader",
  "Not sure yet",
];

const attendanceOptions = [
  { value: "registered_seat", label: "Reserve my seat" },
  { value: "walk_in", label: "I may walk in" },
  { value: "online", label: "I prefer online" },
  { value: "need_help_deciding", label: "Help me decide" },
];

const emptyForm = {
  fullName: "",
  email: "",
  phone: "",
  cityZip: "",
  attendanceType: "registered_seat",
  primaryInterest: "Not sure yet",
  notes: "",
  consent: false,
  website: "",
};

const parseMeetupDate = (meetup) => {
  if (!meetup?.eventDate) return null;
  const date = new Date(`${meetup.eventDate}T${meetup.startTime || "00:00"}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatMeetupDate = (meetup) => {
  const date = parseMeetupDate(meetup);
  if (!date) return "Date to be announced";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatMeetupTime = (meetup) => {
  const start = parseMeetupDate(meetup);
  const end = meetup?.endTime ? new Date(`${meetup.eventDate}T${meetup.endTime}`) : null;
  const formatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  if (!start) return "Time to be announced";
  return end && !Number.isNaN(end.getTime())
    ? `${formatter.format(start)} – ${formatter.format(end)}`
    : formatter.format(start);
};

const toGoogleCalendarDate = (date) =>
  date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

const buildGoogleCalendarLink = (meetup) => {
  const start = parseMeetupDate(meetup);
  if (!start) return "";
  const end = meetup.endTime
    ? new Date(`${meetup.eventDate}T${meetup.endTime}`)
    : new Date(start.getTime() + 60 * 60 * 1000);
  const details = [
    meetup.description || meetup.topic,
    "",
    "Please check your UC confirmation for final venue or meeting-link details.",
  ].join("\n");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: meetup.title || "UC Community Meetup",
    dates: `${toGoogleCalendarDate(start)}/${toGoogleCalendarDate(end)}`,
    details,
    location: [meetup.venueName, meetup.addressLine, meetup.city, meetup.state]
      .filter(Boolean)
      .join(", "),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const CommunityMeetupRegistrationPage = () => {
  const { meetupKey } = useParams();
  const navigate = useNavigate();
  const [meetup, setMeetup] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [submitState, setSubmitState] = useState({ status: "idle", message: "" });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/community-meetups/${encodeURIComponent(meetupKey)}`)
      .then((response) => {
        if (!mounted) return;
        const loadedMeetup = response.data?.meetup;
        setMeetup(loadedMeetup || null);
        setForm({
          ...emptyForm,
          attendanceType:
            loadedMeetup?.onlineAvailable && !loadedMeetup?.walkInAllowed
              ? "online"
              : "registered_seat",
        });
      })
      .catch((error) => {
        if (!mounted) return;
        setSubmitState({
          status: "error",
          message: error?.response?.data?.message || "This UC meetup registration link is not available.",
        });
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [meetupKey]);

  const seatsText = useMemo(() => {
    if (!meetup?.capacity) return "Open registration";
    const remaining = Math.max(0, Number(meetup.capacity) - Number(meetup.registeredCount || 0));
    return `${remaining} of ${meetup.capacity} seats left`;
  }, [meetup]);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submitRegistration = async (event) => {
    event.preventDefault();
    if (!meetup?.id) return;

    if (!form.fullName.trim() || !form.email.trim() || !form.consent) {
      setSubmitState({
        status: "error",
        message: "Please enter your name, email, and consent before registering.",
      });
      return;
    }

    setSubmitState({ status: "submitting", message: "Registering you for the meetup…" });
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/community-meetups/register`,
        {
          ...form,
          meetupId: meetup.id,
          source: "uc_meetup_registration_page",
        }
      );
      setSubmitState({
        status: "success",
        message: response.data?.message || "You are registered for the UC Community Meetup.",
      });
    } catch (error) {
      setSubmitState({
        status: "error",
        message: error?.response?.data?.message || "Unable to register right now.",
      });
    }
  };

  if (loading) {
    return (
      <section className="uc-meetup-registration-page">
        <div className="uc-meetup-registration-card loading">Loading meetup registration…</div>
      </section>
    );
  }

  if (!meetup) {
    return (
      <section className="uc-meetup-registration-page">
        <div className="uc-meetup-registration-card">
          <span className="uc-meetups-kicker">
            <Diversity3 fontSize="inherit" />
            UC Community Meetup
          </span>
          <h1>Registration link not available</h1>
          <p>{submitState.message || "Please choose another upcoming UC community meetup."}</p>
          <Link className="uc-meetup-home-link" to="/2.0/calendar#uc-community-meetups">
            View upcoming meetups
          </Link>
        </div>
      </section>
    );
  }

  const calendarLink = buildGoogleCalendarLink(meetup);

  return (
    <section className="uc-meetup-registration-page">
      <div className="uc-meetup-registration-hero">
        <div>
          <span className="uc-meetups-kicker">
            <Diversity3 fontSize="inherit" />
            UC Community Meetup Registration
          </span>
          <h1>{meetup.title}</h1>
          <p>{meetup.description || meetup.topic}</p>
        </div>
        <div className="uc-meetup-registration-seat-card">
          <strong>{seatsText}</strong>
          <span>{meetup.walkInAllowed ? "Walk-ins welcome, registration preferred" : "Registration required"}</span>
        </div>
      </div>

      <div className="uc-meetup-registration-layout">
        <aside className="uc-meetup-registration-card info">
          <h2>Meetup details</h2>
          <div className="uc-meetup-detail-list">
            <span>
              <CalendarMonth fontSize="small" />
              {formatMeetupDate(meetup)}
            </span>
            <span>
              <Groups fontSize="small" />
              {formatMeetupTime(meetup)}
            </span>
            <span>
              <LocationOn fontSize="small" />
              {[meetup.venueName, meetup.city, meetup.state].filter(Boolean).join(", ") || "Venue to be shared"}
            </span>
          </div>
          <div className="uc-meetup-location registration">
            <strong>{meetup.venueName || "Venue / meeting details"}</strong>
            <small>{meetup.addressLine || "Final room or meeting link will be shared after registration."}</small>
          </div>
          <div className="uc-meetup-tags">
            {meetup.onlineAvailable && <span>Online option</span>}
            {meetup.walkInAllowed && <span>Walk-ins welcome</span>}
            <span>{meetup.topic || "Community session"}</span>
          </div>
        </aside>

        <div className="uc-meetup-registration-card">
          {submitState.status === "success" ? (
            <div className="uc-meetup-success">
              <CheckCircle />
              <h2>Registration received</h2>
              <p>{submitState.message}</p>
              <p>
                Please check your email confirmation. UC may follow up with final venue,
                room, or meeting-link details before the session.
              </p>
              <div className="uc-meetup-success-actions">
                {calendarLink && (
                  <a href={calendarLink} target="_blank" rel="noreferrer">
                    Add to Google Calendar
                  </a>
                )}
                <button type="button" onClick={() => navigate("/2.0/udyogconnect-home")}>
                  Go to UC home
                </button>
              </div>
            </div>
          ) : (
            <>
              <h2>
                <PersonAddAlt fontSize="small" />
                Register for this meetup
              </h2>
              <p className="uc-meetup-registration-note">
                Share your details below so UC can prepare seats, volunteers, and the right
                community lead for your interest.
              </p>
              <form className="uc-meetup-form" onSubmit={submitRegistration}>
                <label className="uc-meetup-honeypot">
                  Website
                  <input
                    value={form.website}
                    onChange={(event) => updateForm("website", event.target.value)}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </label>

                <div className="uc-meetup-form-grid">
                  <label>
                    Full name *
                    <input
                      value={form.fullName}
                      onChange={(event) => updateForm("fullName", event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Email *
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) => updateForm("email", event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Phone
                    <input
                      value={form.phone}
                      onChange={(event) => updateForm("phone", event.target.value)}
                    />
                  </label>
                  <label>
                    City / ZIP
                    <input
                      value={form.cityZip}
                      onChange={(event) => updateForm("cityZip", event.target.value)}
                    />
                  </label>
                  <label>
                    Attendance
                    <select
                      value={form.attendanceType}
                      onChange={(event) => updateForm("attendanceType", event.target.value)}
                    >
                      {attendanceOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Main interest
                    <select
                      value={form.primaryInterest}
                      onChange={(event) => updateForm("primaryInterest", event.target.value)}
                    >
                      {interestOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="uc-meetup-notes">
                  Optional note
                  <textarea
                    value={form.notes}
                    onChange={(event) => updateForm("notes", event.target.value)}
                    placeholder="Tell us what brought you to this meetup."
                  />
                </label>

                <label className="uc-meetup-consent">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(event) => updateForm("consent", event.target.checked)}
                  />
                  <span>
                    I agree UC may contact me about this meetup and related community
                    opportunities.
                  </span>
                </label>

                {submitState.message && (
                  <div className={`uc-meetup-message ${submitState.status}`}>
                    {submitState.message}
                  </div>
                )}

                <button
                  type="submit"
                  className="uc-meetup-submit"
                  disabled={submitState.status === "submitting"}
                >
                  {submitState.status === "submitting" ? "Submitting…" : "Submit registration"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default CommunityMeetupRegistrationPage;
